import { map, isEmpty, find, forEach, filter } from "lodash";
import { Box, H5 } from "orcs-design-system";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import useLazyLoad from "src/hooks/useLazyLoad";
import rethrowUnlessAbort from "src/util/rethrowUnlessAbort";
import { getGroupChildTeamsQuery } from "src/queries/group.graphql";
import TreeViewNext, {
  type TreeNodeData,
  type CustomizeTreeHeaderFn,
} from "src/shared/TreeViewNext";
import GroupsTreeHeader from "./GroupsTreeHeader";
import {
  addChildTeamsToNode,
  convertChildrenToArray,
  createTreeNode,
} from "src/shared/TreeView/TreeView.util";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import { trackEvent } from "src/services/segment";
import { filterOutNonVisibleGroups } from "./index.util";

import useAutoExpandTree from "./useAutoExpandTree";
import useOnNodeSelected from "./useOnNodeSelected";

const noopRefreshTree = () => {};

/** Treebeard-shaped node as produced by buildTreeStructure — extended fields live in ...rest and pass through toTreeData */
type GroupTreeNode = TreeNodeData & {
  toggled?: boolean;
  isParentType?: boolean;
  loading?: boolean;
  group?: { id?: string; hierarchyIds?: string[] };
  children?: GroupTreeNode[];
  active?: boolean;
};

const getNodesToBeLoaded = ({ node }: { node: GroupTreeNode }) => {
  const { isParentType, loading, children } = node;
  let targetNodes: GroupTreeNode[] | null = null;
  if (isParentType && loading) {
    targetNodes = [node];
  } else if (isParentType) {
    targetNodes = children ?? null;
  }
  return filter(targetNodes, "loading") as GroupTreeNode[];
};

type GroupsNavigationProps = {
  root?: GroupTreeNode;
  treeData?: GroupTreeNode | GroupTreeNode[];
  hierarchyIds?: string[] | null;
  navigateTo?: (path: string, ids: string[]) => void;
  refreshTree?: () => void;
  groupTypes?: Record<string, unknown>;
  targetGroupTypes?: Record<string, unknown>;
  sortOption?: string | boolean;
  customizeTreeHeader?: CustomizeTreeHeaderFn<GroupTreeNode>;
  loadChildNodes?: (args: {
    groupIds: string[];
    node?: GroupTreeNode;
  }) => Promise<{ groups: unknown[] }>;
  createTreeNodeImpl?: typeof createTreeNode;
  convertChildrenToArrayImpl?: typeof convertChildrenToArray;
  shouldIgnoreOnToggle?: (node: GroupTreeNode) => boolean;
  addGroupsToRootsImpl?: (...args: unknown[]) => unknown;
  additionalSearchVariables?: Record<string, unknown>;
  activeNodeId?: string;
};

const EMPTY_ADDITIONAL_SEARCH_VARS = {};

const GroupsNavigation = ({
  root,
  treeData,
  hierarchyIds,
  navigateTo,
  refreshTree,
  groupTypes,
  targetGroupTypes,
  sortOption,
  customizeTreeHeader,
  loadChildNodes,
  createTreeNodeImpl = createTreeNode,
  convertChildrenToArrayImpl = convertChildrenToArray,
  shouldIgnoreOnToggle,
  addGroupsToRootsImpl,
  additionalSearchVariables,
  activeNodeId,
}: GroupsNavigationProps) => {
  const stableRefreshTree = refreshTree ?? noopRefreshTree;

  const stableAdditionalSearchVariables = useMemo(
    () => additionalSearchVariables ?? EMPTY_ADDITIONAL_SEARCH_VARS,
    [additionalSearchVariables]
  );

  const loadGroupChildTeams = useLazyLoad({
    query: getGroupChildTeamsQuery,
    additionalVariables: stableAdditionalSearchVariables,
  });

  const loadChildTeams = loadChildNodes || loadGroupChildTeams;

  const toggleCountRef = useRef(0);
  const userCollapsedIdsRef = useRef(new Set<string>());
  const [openStateRevision, setOpenStateRevision] = useState(0);

  useEffect(() => {
    userCollapsedIdsRef.current.clear();
  }, [hierarchyIds]);

  useAutoExpandTree({
    root,
    hierarchyIds,
    groupTypes,
    loadChildTeams,
    refreshTree: stableRefreshTree,
    sortOption,
    createTreeNodeImpl,
    convertChildrenToArrayImpl,
  });

  const onNodeSelected = useOnNodeSelected({ navigateTo });

  const handleNodeToggle = useCallback(
    (node: GroupTreeNode) => {
      if (shouldIgnoreOnToggle?.(node)) {
        return;
      }
      const notLoadedNodes = getNodesToBeLoaded({ node });
      const groupIds = map(notLoadedNodes, "id");

      if (isEmpty(groupIds)) {
        return;
      }

      toggleCountRef.current += 1;
      trackEvent(EVENT_TRACKING.TEAM_CLICKED, {
        action: "team_tree_navigation_expanded",
        totalToggles: toggleCountRef.current,
      });

      loadChildTeams({
        groupIds,
        ...(loadChildNodes ? { node } : {}),
      })
        .then(({ groups }) => {
          const filteredGroups = filterOutNonVisibleGroups(groups);
          forEach(notLoadedNodes, (childNode) => {
            const targetGroup = find(
              filteredGroups,
              (g: { id?: string }) => g.id === childNode.id
            );
            addChildTeamsToNode(
              targetGroup,
              childNode,
              groupTypes,
              sortOption,
              targetGroupTypes,
              createTreeNodeImpl,
              convertChildrenToArrayImpl,
              addGroupsToRootsImpl
            );
          });
          stableRefreshTree();
        })
        .catch(rethrowUnlessAbort);
    },
    [
      loadChildTeams,
      loadChildNodes,
      stableRefreshTree,
      groupTypes,
      sortOption,
      targetGroupTypes,
      createTreeNodeImpl,
      convertChildrenToArrayImpl,
      addGroupsToRootsImpl,
      shouldIgnoreOnToggle,
    ]
  );

  const handleNodeSelected = useCallback(
    (node: GroupTreeNode) => {
      const teamId = node.group?.id ?? node.id;
      if (activeNodeId != null && teamId === activeNodeId) {
        return;
      }
      onNodeSelected(node);
    },
    [onNodeSelected, activeNodeId]
  );

  const handleChevronToggle = useCallback(
    (node: GroupTreeNode, isOpen: boolean) => {
      if (isOpen) {
        userCollapsedIdsRef.current.delete(node.id);
      } else {
        userCollapsedIdsRef.current.add(node.id);
      }
      node.toggled = isOpen;
      setOpenStateRevision((revision) => revision + 1);
      if (isOpen) {
        handleNodeToggle(node);
      }
    },
    [handleNodeToggle]
  );

  const getNodeOpenState = useCallback((node: GroupTreeNode) => {
    if (userCollapsedIdsRef.current.has(node.id)) {
      return false;
    }
    return typeof node.toggled === "boolean" ? node.toggled : undefined;
  }, []);

  const defaultHeader = useCallback<CustomizeTreeHeaderFn<GroupTreeNode>>(
    ({ node }) => (
      <GroupsTreeHeader node={node} groupTypes={groupTypes ?? {}} />
    ),
    [groupTypes]
  );
  const resolvedHeader = customizeTreeHeader ?? defaultHeader;

  const openStateStructureKey = useMemo(() => {
    if (treeData == null) {
      return "empty";
    }
    const nodes = Array.isArray(treeData) ? treeData : [treeData];
    const collectKeys = (node: GroupTreeNode): string[] => {
      const toggled =
        typeof node.toggled === "boolean" ? String(node.toggled) : "-";
      const childKeys = (node.children ?? []).flatMap(collectKeys);
      return [`${node.id}:${toggled}`, ...childKeys];
    };
    return `${openStateRevision}|${nodes.flatMap(collectKeys).join("|")}`;
  }, [treeData, openStateRevision]);

  return (
    <Box>
      {isEmpty(treeData) && <H5>No teams data available</H5>}
      <TreeViewNext<GroupTreeNode>
        data={treeData}
        onNodeSelected={handleNodeSelected}
        onChevronToggle={handleChevronToggle}
        customizeTreeHeader={resolvedHeader}
        activeNodeId={activeNodeId}
        openByDefault={false}
        getNodeOpenState={getNodeOpenState}
        openStateStructureKey={openStateStructureKey}
        syncOpenStateOnDataChange={false}
        openStateSyncDelayMs={0}
      />
    </Box>
  );
};

export default React.memo(GroupsNavigation);
